import { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSapiensAuth } from '@sapiens/auth-react';
import { useSnackbar } from 'notistack';
import { AuthLayout } from '@/components/layout';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, saveCredentials } = useSapiensAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login({ email, password, rememberMe });

      // Save credentials if remember me is checked
      if (rememberMe) {
        await saveCredentials(email, password);
      }

      enqueueSnackbar(t('auth.loginSuccess'), { variant: 'success' });
      navigate(from, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.loginError');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title={t('auth.login')} subtitle={t('app.name')}>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label={t('auth.email')}
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label={t('auth.password')}
          type="password"
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
        />

        <FormControlLabel
          control={
            <Checkbox
              value="remember"
              color="primary"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={loading}
            />
          }
          label={t('auth.rememberMe')}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading || !email || !password}
        >
          {loading ? t('common.loading') : t('auth.login')}
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <MuiLink component={Link} to="/recovery" variant="body2">
            {t('auth.forgotPassword')}
          </MuiLink>
          <MuiLink component={Link} to="/activation" variant="body2">
            {t('activation.title')}
          </MuiLink>
        </Box>
      </Box>
    </AuthLayout>
  );
}
